import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import toothIcon from 'assets/images/icons/icon-tooth.png';
import { selectDataMatch } from 'connect/selectors';
import {
  doesProviderOfferSpecialty,
  getRequestedSpecialties,
  getRequestedSpecialtiesOffered,
  getSpecialties,
} from 'pages/results/helpers/specialty-helpers';

import { uniqueBy } from '../../../../../utils/array';
import { ANY_DENTIST_OPTION } from '../constants';

import { OptionContent } from './OptionContent';
import {
  OptionGroupInDropdown,
  OptionGroupInSelect,
  StyledSelect,
} from './styled-components';

const { Option } = StyledSelect;

const getProviderOptionData = (provider) => ({
  id: `${provider.id}`,
  text: provider.doctor.name,
  images: [provider.doctor.primaryPhoto],
  isInstantBook: provider.isInstantBook,
});

const renderOption = (option) => {
  if (option.group) {
    return (
      <>
        <Option key={option.group} value={option.id}>
          <OptionGroupInSelect>
            <OptionContent
              text={`All ${option.group} Availability`}
              localImages={[toothIcon]}
            />
          </OptionGroupInSelect>
          <OptionGroupInDropdown>
            <OptionContent text={option.group} />
          </OptionGroupInDropdown>
        </Option>
        {option.providers.map((provider) => (
          <Option key={`${provider.id}-${option.group}`} value={provider.id}>
            <OptionContent
              text={provider.text}
              images={provider.images}
              isInstantBook={provider.isInstantBook}
            />
          </Option>
        ))}
      </>
    );
  }

  return (
    <Option key={option.id} value={option.id}>
      <OptionContent
        text={option.text}
        localImages={option.localImages}
        images={option.images}
        isInstantBook={option.isInstantBook}
      />
    </Option>
  );
};

const ANY_DENTIST_OPTION_DATA = {
  id: ANY_DENTIST_OPTION,
  text: 'All Dentist Availability',
  localImages: [toothIcon],
  providers: [],
};

export const ProviderDropdown = ({ handleSelect, providers }) => {
  const match = useSelector(selectDataMatch);
  const [defaultValue, setDefaultValue] = useState();

  const requestedSpecialties = useMemo(() => getRequestedSpecialties(match), [
    match,
  ]);

  const specialties = getSpecialties({ providers });

  const getProvidersWithSpecialty = (specialty) =>
    uniqueBy(providers, 'id').filter((provider) => {
      // No specialty specified, return all providers without a specialty
      if (!specialty) {
        return !provider.specialties?.length;
      }

      return doesProviderOfferSpecialty(provider, specialty);
    });

  const getGroupForSpecialty = (specialty) => {
    const providersWithSpecialty = getProvidersWithSpecialty(specialty)
      .map(getProviderOptionData)
      .sort((a, b) => a.text.localeCompare(b.text));

    return {
      group: specialty.nameAlt || specialty.name,
      id: specialty.id,
      providers: providersWithSpecialty,
    };
  };

  const options = [
    ANY_DENTIST_OPTION_DATA,

    // Providers with specialties
    ...specialties.map(getGroupForSpecialty),

    // Providers without specialties
    ...getProvidersWithSpecialty(null)
      .map(getProviderOptionData)
      .sort((a, b) => a.text.localeCompare(b.text)),
  ];

  useEffect(() => {
    const offeredRequestedSpecialties = getRequestedSpecialtiesOffered(
      specialties,
      requestedSpecialties,
    );

    const requestedSpecialtyId = offeredRequestedSpecialties?.find((s) => s.dbId)
      ?.dbId;

    if (requestedSpecialtyId) {
      setDefaultValue(requestedSpecialtyId);
      handleSelect(requestedSpecialtyId);
      return;
    }

    setDefaultValue(ANY_DENTIST_OPTION);
    handleSelect(ANY_DENTIST_OPTION);
  }, [requestedSpecialties]);

  // Only update the rendered options when the options change
  const renderedOptions = useMemo(() => options.map(renderOption), [options]);

  if (!defaultValue) {
    return null;
  }

  return (
    <StyledSelect
      onSelect={handleSelect}
      defaultValue={defaultValue}
      aria-label="Choose a dentist"
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
    >
      {renderedOptions}
    </StyledSelect>
  );
};

ProviderDropdown.propTypes = {
  handleSelect: PropTypes.func,
  providers: PropTypes.array.isRequired,
};
